import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { videoApi } from '@carrent/api'
import { useAuthStore } from '@carrent/core'
import { PageHeader, Icon, EventTime, JitsiRoom } from '@carrent/ui'
import { CALL_PRIVACIES, CALL_STATUSES, isJoinable, useVideoCall, useVideoSettings } from '@carrent/domain'


/**
 * Карточка звонка и сам звонок.
 *
 * Вход двухфазный: карточка → кнопка «Войти» → POST /token → iframe.
 * Jitsi-токен живёт минуты, поэтому берётся строго по нажатию,
 * а не при открытии страницы: карточку можно разглядывать час.
 *
 * Чужой парольный звонок спрашивает пароль здесь же (403 → поле).
 * Отозванный/истёкший показывает конечное состояние вместо кнопки.
 */

function CopyLinkButton({ callId }) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    const url = `${window.location.origin}/call/${callId}`
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      window.prompt('Скопируйте ссылку', url) // clipboard недоступен по http
    }
  }
  return (
    <button type="button" className="btn btn--outline btn--sm" onClick={copy}>
      <Icon name="file" size={14} /> {copied ? 'Скопировано' : 'Ссылка для гостей'}
    </button>
  )
}

function InvitesPanel({ callId, initialIssued = null }) {
  const [names, setNames] = useState('')
  const [issued, setIssued] = useState(initialIssued)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  const submit = async () => {
    const guests = names
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean)
      .map((name) => ({ name }))
    if (!guests.length) return
    setSaving(true)
    setError('')
    try {
      const { data } = await videoApi.invites(callId, guests)
      setIssued(data.data.invites)
      setNames('')
    } catch (e) {
      setError(e.response?.data?.message || 'Не удалось создать приглашения')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="card flex-col gap-sm">
      <h2 className="section-title">Гостевые приглашения</h2>
      <p className="text-muted video-hint">
        Ссылки персональные и показываются один раз — скопируйте их сразу.
      </p>
      <textarea
        className="input"
        rows={2}
        placeholder="Имена через запятую: Страховой комиссар, Арендодатель"
        value={names}
        onChange={(e) => setNames(e.target.value)}
      />
      {error && <div className="alert alert--error">{error}</div>}
      <button
        type="button"
        className="btn btn--secondary btn--sm video-self-start"
        onClick={submit}
        disabled={saving || !names.trim()}
      >
        {saving ? <span className="spinner spinner--sm" /> : null} Создать ссылки
      </button>

      {issued && (
        <div className="flex-col gap-sm">
          {issued.map((inv) => {
            const url = `${window.location.origin}/call/${callId}?t=${inv.invite_token}`
            return (
              <div className="field" key={inv.invite_token}>
                <span className="field__label">{inv.name}</span>
                <input className="input" readOnly value={url} onFocus={(e) => e.target.select()} />
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}

function RecordingsPanel({ callId, enabled }) {
  const [items, setItems] = useState(null)

  useEffect(() => {
    if (!enabled) return undefined
    let alive = true
    videoApi.recordings(callId)
      .then(({ data }) => alive && setItems(data.data))
      .catch(() => alive && setItems([]))
    return () => { alive = false }
  }, [callId, enabled])

  if (!enabled || !items || items.length === 0) return null

  return (
    <section className="card flex-col gap-sm">
      <h2 className="section-title">Записи</h2>
      {items.map((rec) => (
        <div key={rec.id} className="flex-row video-row-between">
          <span>
            Запись от <EventTime value={rec.created_at} />
            {rec.size_bytes ? ` · ${Math.round(rec.size_bytes / 1048576)} МБ` : ''}
          </span>
          {rec.url && (
            <a className="btn btn--outline btn--sm" href={rec.url}
               target="_blank" rel="noreferrer">
              Открыть
            </a>
          )}
        </div>
      ))}
      <p className="text-muted video-hint video-hint--0">
        Ссылки временные (час) — страница обновит их при открытии.
      </p>
    </section>
  )
}

export default function VideoCallPage() {
  const { id } = useParams()
  const location = useLocation()
  const { user } = useAuthStore()
  const { settings } = useVideoSettings()
  const { call, loading, notFound, refresh, setCall } = useVideoCall(id)

  const [token, setToken] = useState(null)
  const [password, setPassword] = useState('')
  const [needPassword, setNeedPassword] = useState(false)
  const [joinError, setJoinError] = useState('')
  const [acting, setActing] = useState(false)
  const [snapshotMsg, setSnapshotMsg] = useState('')
  const jitsiApi = useRef(null)

  // Стоп-кадр видеоосмотра: кадр снимает клиент (сервер видео
  // не видит), фото ложится к поездке в категорию «Доп.».
  const takeSnapshot = async () => {
    const api = jitsiApi.current
    if (!api) return
    try {
      const shot = await api.captureLargeVideoScreenshot()
      const dataUrl = shot?.dataURL
      if (!dataUrl) throw new Error('Кадр пустой — дождитесь видео')
      await videoApi.snapshot(call.id, dataUrl)
      setSnapshotMsg('Кадр сохранён в фото поездки')
      setTimeout(() => setSnapshotMsg(''), 2500)
    } catch (e) {
      setSnapshotMsg(e.response?.data?.message || e.message || 'Не удалось сохранить кадр')
      setTimeout(() => setSnapshotMsg(''), 4000)
    }
  }

  const join = useCallback(async () => {
    setJoinError('')
    try {
      const { data } = await videoApi.token(id, password || undefined)
      setToken(data.data)
      setCall(data.data.call)
    } catch (e) {
      const status = e?.response?.status
      if (status === 403) {
        setNeedPassword(true)
        if (password) setJoinError('Неверный пароль')
      } else {
        setJoinError(e.response?.data?.message || 'Не удалось войти в звонок')
      }
    }
  }, [id, password, setCall])

  const act = (action) => async () => {
    setActing(true)
    try {
      const { data } = await videoApi[action](id)
      setCall(data.data)
      if (action === 'end' || action === 'revoke') {
        jitsiApi.current?.executeCommand('hangup')
        setToken(null)
      }
    } finally {
      setActing(false)
    }
  }

  const isModerator =
    user && call && (user.role === 'admin' || user.role === 'manager'
      || call.created_by?.id === user.id)

  if (loading) {
    return (
      <div className="p-page flex-col gap">
        <PageHeader title="Видеозвонок" back />
        <div className="card"><span className="skeleton table__skeleton-row" /></div>
      </div>
    )
  }

  if (notFound || !call) {
    return (
      <div className="p-page flex-col gap">
        <PageHeader title="Видеозвонок" back />
        <div className="state">
          <p className="state__title">Звонок не найден</p>
        </div>
      </div>
    )
  }

  // Внутри конференции: экран отдан комнате целиком.
  if (token) {
    return (
      <div className="p-page flex-col gap">
        <PageHeader
          title={call.title}
          back
          right={
            isModerator ? (
              <span className="flex-row gap-sm video-actions">
                {call.trip_id && (
                  <button
                    type="button"
                    className="btn btn--outline btn--sm"
                    onClick={takeSnapshot}
                    title="Кадр текущего видео — в фото поездки"
                  >
                    Стоп-кадр
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn--danger btn--sm"
                  onClick={act('end')}
                  disabled={acting}
                >
                  Завершить для всех
                </button>
              </span>
            ) : undefined
          }
        />
        {snapshotMsg && <div className="alert alert--success">{snapshotMsg}</div>}
        <JitsiRoom
          token={token}
          subject={call.title}
          roomConfig={{
            chat_enabled: settings.chat_enabled,
            screenshare: settings.screenshare_enabled,
            start_muted_after: settings.start_muted_after,
            recording: settings.recording_enabled === true,
          }}
          lobby={Boolean(
            isModerator
            && (call.privacy === 'invite'
              || (settings.lobby_for_guests && settings.guest_access))
          )}
          onClose={() => {
            videoApi.leave(call.id)
            setToken(null)
            refresh()
          }}
          onApiReady={(api) => { jitsiApi.current = api }}
        />
      </div>
    )
  }

  const st = CALL_STATUSES[call.status] || { label: call.status }

  return (
    <div className="p-page flex-col gap">
      <PageHeader title={call.title} back />

      <section className="card flex-col gap-sm">
        <div className="flex-row gap-sm video-list-item video-actions">
          <span className={`badge ${call.status === 'active' ? 'badge--success' : ''}`}>
            {st.label}
          </span>
          <span className="text-muted video-hint">
            {CALL_PRIVACIES[call.privacy]?.label}
            {' · действует до '}<EventTime value={call.expires_at} />
          </span>
        </div>

        {call.created_by && (
          <p className="text-muted video-hint video-hint--0">
            Создал(а) {call.created_by.full_name}, <EventTime value={call.created_at} />
          </p>
        )}

        {call.description && (
          <p className="video-call-desc">{call.description}</p>
        )}

        {isJoinable(call) ? (
          <div className="flex-col gap-sm">
            {needPassword && (
              <div className="field video-form-medium">
                <label className="field__label" htmlFor="join-password">Пароль звонка</label>
                <input
                  id="join-password"
                  className="input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && join()}
                />
              </div>
            )}
            {joinError && <div className="alert alert--error">{joinError}</div>}
            <div className="flex-row gap-sm video-actions">
              <button type="button" className="btn btn--primary" onClick={join}>
                <Icon name="chat" size={14} /> Войти в звонок
              </button>
              <CopyLinkButton callId={call.id} />
              {isModerator && (
                <>
                  <button
                    type="button"
                    className="btn btn--outline btn--sm"
                    onClick={act('extend')}
                    disabled={acting}
                    title="Продлить на 4 часа"
                  >
                    Продлить
                  </button>
                  <button
                    type="button"
                    className="btn btn--danger btn--sm"
                    onClick={act('revoke')}
                    disabled={acting}
                  >
                    Отозвать
                  </button>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="state state--compact">
            <p className="state__title">
              {call.status === 'revoked' && 'Звонок отозван'}
              {call.status === 'expired' && 'Ссылка истекла'}
              {call.status === 'ended' && 'Звонок завершён'}
            </p>
            {isModerator && call.status !== 'revoked' && (
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={act('extend')}
                disabled={acting}
              >
                Продлить и открыть снова
              </button>
            )}
          </div>
        )}
      </section>

      {isModerator && call.privacy === 'invite' && isJoinable(call) && (
        <InvitesPanel callId={call.id} initialIssued={location.state?.invites || null} />
      )}

      <RecordingsPanel callId={call.id} enabled={settings.recording_enabled === true} />

      {call.participants?.length > 0 && (
        <section className="card flex-col gap-sm">
          <h2 className="section-title">Участники</h2>
          {call.participants.map((p) => (
            <div key={p.id} className="flex-row video-row-between">
              <span>{p.user?.full_name || p.guest_name}</span>
              <span className="text-muted video-hint">
                {p.joined_at ? <>вошёл(ла) <EventTime value={p.joined_at} /></> : 'приглашён(а)'}
              </span>
            </div>
          ))}
        </section>
      )}
    </div>
  )
}
